<template>
    <VPage>
        <VPageHeader show-third-block :title="pageTitle">
            <template v-slot:second>
                <div class="app-page-header__column app-page-header__column--tools">
                    <VTaskFilter
                        v-model="filters"
                        class="prevent-close"
                        :filter-types="[
                            TaskFilterTypes.Author,
                            TaskFilterTypes.Status,
                            TaskFilterTypes.Assignee,
                            TaskFilterTypes.Approver,
                            TaskFilterTypes.Contributor,
                            TaskFilterTypes.AuthorOrAssignee,
                            TaskFilterTypes.Deadline,
                            TaskFilterTypes.CreationDate,
                        ]"
                    ></VTaskFilter>

                    <VButtonDropdown class="prevent-close" :options="regularActions" placement="left-start">
                        <DotsSvg class="rotate-90"></DotsSvg>
                    </VButtonDropdown>

                    <VSearchField v-model="searchString" class="prevent-close" debounce-mode></VSearchField>
                </div>
            </template>
        </VPageHeader>

        <VPageContent class="" vertical>
            <VTable v-model:order="order" v-model:table="table" class="numerics__tasks-table">
                <VTableDataSet v-if="tasks.length">
                    <template v-for="(task, index) in tasks" :key="task.id">
                        <template v-if="task.type === TaskType.Goal">
                            <RouterLink
                                class="task-selector"
                                :task-id="task.id"
                                :to="{ name: 'goals.view', params: { goalId: task.id } }"
                            >
                                <VTableRow
                                    :active="task.id.toString() == route.query.task"
                                    :data="task"
                                    :index="index"
                                    :table="table"
                                    @contextmenu="openContextMenu($event, task)"
                                ></VTableRow>
                            </RouterLink>
                        </template>

                        <template v-else>
                            <RouterLink
                                class="task-selector prevent-close"
                                :task-id="task.id"
                                :to="{ query: { task: task.id } }"
                            >
                                <VTableRow
                                    :active="task.id.toString() == route.query.task"
                                    :data="task"
                                    :index="index"
                                    :offset="+hasLoadedParent(task) * 3"
                                    :table="table"
                                    @contextmenu="openContextMenu($event, task)"
                                ></VTableRow>
                            </RouterLink>
                        </template>
                    </template>
                </VTableDataSet>
            </VTable>
        </VPageContent>
    </VPage>
</template>

<script setup lang="ts">
// Svg
import DotsSvg from '@/assets/dots.svg';
import DownloadSvg from '@/assets/download.svg';

// Components
import VTaskFilter, { TaskFilterTypes } from '../components/VTaskFilter.vue';
import VSearchField from '../components/VSearchField.vue';
import VPage from '../components/VPage.vue';
import VPageHeader from '../components/VPageHeader.vue';
import VPageContent from '../components/VPageContent.vue';
import VTable from '@/components/VTable.vue';
import VTableRow from '@/components/VTableRow.vue';
import VTableDataSet from '@/components/VTableDataSet.vue';
import VButtonDropdown from '../components/VButtonDropdown.vue';

// Widgets

// Other
import { useI18n } from 'vue-i18n';
import { computed, markRaw, onMounted, ref, toRaw, watch } from 'vue';
import Storages from '@/core/Storages';
import { IOrder } from '@/core/Values/IOrder';
import Settings from '@/core/Settings';
import TaskService, { ExportTaskRequest, QueryTaskRequest } from '@/core/Services/TaskService';
import ITask from '@/core/Models/ITask';
import UserMapper from '@/core/UserMapper';
import ObjectStorageMapper from '@/core/ObjectStorageMapper';
import MutationBus from '@/core/Mutations/MutationBus';
import IMutatorContext from '@/core/Mutations/IMutatorContext';
import { TaskMutatorContext } from '@/core/Mutators/TaskMutator';
import store from '@/store';
import { useTaskContextMenu } from '@/mixins/TaskApi';
import useTaskTableViewer, { journalColumns } from '@/mixins/TaskTableViewer';
import { useRoute } from 'vue-router';
import orderBy from 'lodash.orderby';
import TaskType from '@/core/Values/TaskType';
import { IDropdownOption } from '@/core/Values/IDropdownOption';
import { downloadFile } from '@/utils/utils';
import TaskExportSchema from '@/core/Values/TaskExportSchema';
import { setPageTitle } from '@/utils/document-utils';

const { t } = useI18n();
const route = useRoute();
const { openContextMenu } = useTaskContextMenu();
const table = useTaskTableViewer(Settings.UI.Table + '.tasks', {
    header: true,
    columns: journalColumns,
});

let goalsAndTasks = ref([] as ITask[]);
let searchString = ref('');
let mutatorContext = null as IMutatorContext | null;
let currentUser = store.state.user;
let pageTitle = t('tasks');
let regularActions: IDropdownOption[] = [
    {
        icon: markRaw(DownloadSvg),
        title: t('export'),
        action: () => {
            downloadFile('/api/v1/tasks/export', {
                ...tasksFilters.value,
                withColumns: table.value.columns.filter((column) => column.visible).map((column) => column.key),
                schema: TaskExportSchema.GroupedByParent,
                language: store.state.userSettings.locale,
            } as ExportTaskRequest);
        },
    },
];

const order = computed(
    Storages.Filters.computed<IOrder>(Settings.UI.Order + '.tasks', {
        orderBy: 'id',
        orderAscending: false,
    }),
);
const filters = computed(Storages.Filters.computed(Settings.UI.Filters + '.tasks', { value: [], formattedValue: {} }));
const tasks = computed(() => {
    const childrenByParentId = new Map<number, ITask[]>();
    const taskIds = new Set<number>();
    const result: ITask[] = [];
    const orderColumn = toRaw(order).value.orderBy as keyof ITask;
    const orderAscending = toRaw(order).value.orderAscending ?? false;

    orderBy(goalsAndTasks.value, orderColumn, orderAscending ? 'asc' : 'desc').forEach((task) => {
        taskIds.add(task.id);
        const key = task.parentId ?? -1;
        const group = childrenByParentId.get(key);
        if (!group) {
            childrenByParentId.set(key, [task]);
        } else {
            group.push(task);
        }
    });

    const roots = [...(childrenByParentId.get(-1) ?? [])];

    // Show children as roots if parent does not exist
    childrenByParentId.forEach((group, parentId) => {
        if (parentId !== -1 && !taskIds.has(parentId)) {
            roots.push(...group);
        }
    });

    return roots.reduce((carry, task) => {
        const children = childrenByParentId.get(task.id);
        return children ? carry.concat(task, children) : carry.concat(task);
    }, result);
});

const tasksFilters = computed(
    (): Partial<QueryTaskRequest> => ({
        ...order.value,
        ...filters.value.formattedValue,
        includes: ['comments-count', 'attachments-count', 'approvements', 'collaborators'],
        search: searchString.value ?? undefined,
    }),
);

watch(() => tasksFilters.value, fetchData, { immediate: true });

onMounted(() => {
    setPageTitle(pageTitle);
});

async function fetchData(): Promise<void> {
    goalsAndTasks.value = await TaskService.queryAsync(tasksFilters.value);

    // Attaching of users and preview to tasks.
    UserMapper.mapTasksAsync(goalsAndTasks.value);
    ObjectStorageMapper.mapTasksAsync(goalsAndTasks.value);

    MutationBus.deactivate(mutatorContext);
    mutatorContext = markRaw(
        new TaskMutatorContext(goalsAndTasks.value, {
            mapUsers: true,
            mapPreview: false,
            // Fetches a task if the patch contains the changes that can affect of the filters.
            fetchTask: async (patch: Partial<ITask>) => {
                if (!TaskService.matchPatch(patch, currentUser?.id as string, tasksFilters.value)) {
                    return undefined;
                }

                const tasks = await TaskService.queryAsync({
                    ...tasksFilters.value,
                    whereId: [patch.id as number],
                    perPage: 1,
                });

                if (!tasks.length) {
                    return undefined;
                }

                return UserMapper.mapTaskAsync(tasks[0]);
            },

            // Excludes tasks if they are not matching the specified filters.
            excludeTask: (task: ITask) => !TaskService.match(task, currentUser?.id as string, tasksFilters.value),
            // Ignores tasks creation if they are not matching the specified filters.
            ignoreTaskCreating: (task: ITask) => TaskService.match(task, currentUser?.id as string, tasksFilters.value),
        }),
    );
    MutationBus.activate(mutatorContext);
}

function hasLoadedParent(task: ITask): boolean {
    if (!task.parentId) {
        return false;
    }

    return goalsAndTasks.value.some((item) => item.id === task.parentId);
}
</script>

<style lang="scss">
.numerics {
    &__tasks-table {
        flex-shrink: 0;
        height: auto;
    }
}
</style>
